// Mobile Header Component

import { Avatar, Button, Dropdown, DropdownItem, DropdownMenu, DropdownSection, DropdownTrigger, Chip } from "@heroui/react";
import { Menu, LogOut, User, HelpCircle, Shield, Smartphone, CheckCircle } from "lucide-react";
import Logo from "./Logo";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@src/presentation/contexts/AuthContext";
import { useSidebar } from "@src/presentation/contexts/SidebarContext";
import { CreditBadge } from "@src/presentation/components/CreditBadge";
import { useWhatsAppLink } from "@src/presentation/contexts/WhatsAppLinkContext";


export function Navbar() {
  const navigate = useNavigate();
  const { toggleSidebar, screenType } = useSidebar();
  const isMobileOrTablet = screenType === "mobile" || screenType === "tablet";
  const { isAuthenticated, user, logout } = useAuth();
  const { openLinkModal, needsLinking, isLinked } = useWhatsAppLink();

  // Mock notification count - in real app, this would come from an API
  // const notificationCount = 3;

  const handleMenuItemClick = (route: string) => {
    navigate(route);
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };


  return (
    <div className="fixed top-0 left-0 right-0 z-[401] h-14 px-2 border-b border-border bg-secondary flex items-center justify-between">
      <div>
        {isMobileOrTablet && (
          <Button variant="ghost" size="sm" onPress={toggleSidebar}>
            <Menu className="h-5 w-5 text-gray-600" />
          </Button>
        )}
        <Logo />
      </div>

      <div className="pr-4">
        {isAuthenticated ? (
          <div className="flex items-center gap-4">
            {/* Credit Balance */}
            <CreditBadge showLabel={!isMobileOrTablet} />

            {/* Notifications Bell */}
            {/* <Button
              isIconOnly
              variant="light"
              size="sm"
              onPress={() => navigate('/notifications')}
              className="relative"
            >
              {notificationCount > 0 ? (
                <Badge content={notificationCount} color="danger" size="sm" placement="top-right">
                  <Bell className="h-5 w-5 text-default-600" />
                </Badge>
              ) : (
                <Bell className="h-5 w-5 text-default-600" />
              )}
            </Button> */}

            {/* User Dropdown */}
            <Dropdown showArrow placement="bottom-end" className="relative rounded-none">
              <DropdownTrigger>
                <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
                  <Avatar
                    size="sm"
                    src={user?.profilePictureUrl || undefined}
                    name={user?.displayName || 'User'}
                    className="cursor-pointer"
                    showFallback
                  />
                  {!isMobileOrTablet && (
                    <div className="flex flex-col items-start">
                      <span className="text-sm font-medium text-foreground">
                        {user?.displayName || 'User'}
                      </span>
                      <span className="text-xs text-default-500">
                        {user?.roles && user.roles.length > 0 ? user.roles[0].displayName : 'User'}
                      </span>
                    </div>
                  )}
                </div>
              </DropdownTrigger>
              <DropdownMenu
                classNames={{
                  base: "text-primary min-w-[220px]",
                }}
              >
                {/* User Info Section */}
                <DropdownSection
                  title="Signed in as"
                  classNames={{
                    heading: "text-xs font-semibold text-default-400 uppercase",
                  }}
                >
                  <DropdownItem
                    key="user-info"
                    className="h-14 gap-2"
                    textValue="User info"
                    isReadOnly
                    classNames={{
                      base: "rounded",

                    }}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold">{user?.displayName || 'User'}</span>
                      <span className="text-xs text-default-500">{user?.email || 'user@pepperbot.com'}</span>
                    </div>
                  </DropdownItem>
                </DropdownSection>

                {/* Account Actions */}
                <DropdownSection
                  title="Account"
                  classNames={{
                    heading: "text-xs font-semibold text-default-400 uppercase",
                  }}
                >
                  <DropdownItem
                    key="profile"
                    className="hover:text-foreground rounded"
                    onPress={() => handleMenuItemClick('/profile')}
                    startContent={<User size={16} />}
                  >
                    My Profile
                  </DropdownItem>

                  {/* <DropdownItem
                    key="account-settings"
                    className="hover:text-foreground rounded"
                    onPress={() => handleMenuItemClick('/account/settings')}
                    startContent={<Settings size={16} />}
                  >
                    Account Settings
                  </DropdownItem> */}

                  <DropdownItem
                    key="security"
                    className="hover:text-foreground rounded"
                    onPress={() => handleMenuItemClick('/account/security')}
                    startContent={<Shield size={16} />}
                  >
                    Security
                  </DropdownItem>

                  {/* WhatsApp Linking - Show based on context state */}
                  {needsLinking ? (
                    <DropdownItem
                      key="link-whatsapp"
                      className="hover:text-foreground rounded text-[#25D366]"
                      onPress={openLinkModal}
                      startContent={<Smartphone size={16} className="text-[#25D366]" />}
                      endContent={
                        <Chip size="sm" variant="flat" color="warning" className="text-[10px] h-5">
                          Link
                        </Chip>
                      }
                    >
                      Link WhatsApp
                    </DropdownItem>
                  ) : isLinked ? (
                    <DropdownItem
                      key="whatsapp-linked"
                      className="hover:text-foreground rounded"
                      isReadOnly
                      startContent={<CheckCircle size={16} className="text-[#25D366]" />}
                      endContent={
                        <Chip size="sm" variant="flat" className="text-[10px] h-5 bg-[#25D366]/10 text-[#25D366]">
                          Linked
                        </Chip>
                      }
                    >
                      WhatsApp Connected
                    </DropdownItem>
                  ) : null}
                </DropdownSection>

                {/* Help & Support */}
                <DropdownSection
                  title="Support"
                  classNames={{
                    heading: "text-xs font-semibold text-default-400 uppercase",
                  }}
                >
                  {/* <DropdownItem
                    key="notifications"
                    className="hover:text-foreground rounded"
                    onPress={() => handleMenuItemClick('/notifications')}
                    startContent={<Bell size={16} />}
                    endContent={
                      notificationCount > 0 ? (
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-danger text-xs text-white">
                          {notificationCount}
                        </span>
                      ) : null
                    }
                  >
                    Notifications
                  </DropdownItem> */}

                  <DropdownItem
                    key="help"
                    className="hover:text-foreground rounded"
                    onPress={() => handleMenuItemClick('/help')}
                    startContent={<HelpCircle size={16} />}
                  >
                    Help Center
                  </DropdownItem>
                </DropdownSection>

                {/* Logout */}
                <DropdownSection>
                  <DropdownItem
                    key="logout"
                    className="text-danger hover:bg-danger/10 rounded"
                    color="danger"
                    onPress={handleLogout}
                    startContent={<LogOut size={16} />}
                  >
                    Logout
                  </DropdownItem>
                </DropdownSection>
              </DropdownMenu>
            </Dropdown>
          </div>
        ) : (
          <Link to={"/auth"} className="text-sm font-medium text-primary underline hover:text-foreground">
            Login
          </Link>
        )}
      </div>
    </div>
  );
}
